import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import CopyIcon from '@mui/icons-material/FileCopy'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  DataGrid,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridRenderCellParams,
  GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid'
import { useSnackbar } from 'notistack'
import React, { FC, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import {
  DtoolRelationType,
  HardwareType,
  MarkupPropsType,
  MarkupType,
  MaterialGroupType,
  MaterialType,
} from '../../api/appAPI'
import { HardwareProps } from '../../classes/Hardware'
import { saveGridStateInLocalStorage } from '../../common/helpers'
import {
  addHardware,
  addMarkup,
  copyHardware,
  deleteHardware,
  editHardware,
} from '../../redux/itemReducer'
import { StateType } from '../../redux/store'
import { EnqueueSnackbarType } from '../common/Helpers'
import { actions as appActions } from './../../redux/appReducer'
import DefineHardware from './DefineHardware'
import EditHardware from './EditHardware'

interface IProps {
  hardwaresList: HardwareType[]
  markups: MarkupType[]
  materials: MaterialType[]
  materialGroups: MaterialGroupType[]
  hardwareGridVisibilityModel: GridColumnVisibilityModel | undefined
  hardwareGridSortModel: GridSortModel | undefined
  hardwareGridFilterModel: GridFilterModel | undefined
  activeHardwareId: number
  dtoolRelationsList: DtoolRelationType[]

  addHardware(
    hardwareProps: HardwareProps,
    enqueueSnackbar: EnqueueSnackbarType
  ): void
  editHardware(
    hardware: HardwareType,
    enqueueSnackbar: EnqueueSnackbarType
  ): void
  deleteHardware(id: number, enqueueSnackbar: EnqueueSnackbarType): void
  copyHardware(id: number, enqueueSnackbar: EnqueueSnackbarType): void
  addMarkup(markup: MarkupPropsType, enqueueSnackbar: EnqueueSnackbarType): void
  setHardwareGridVisibilityModel(
    model: GridColumnVisibilityModel | undefined
  ): void
  setHardwareGridSortModel(model: GridSortModel | undefined): void
  setHardwareGridFilterModel(model: GridFilterModel | undefined): void
  setActiveHardwareId(value: number): void
  openMaterialDetails(id: number): void
  openMaterialGroupDetails(id: number): void
}

const HardwareTab: FC<IProps> = (props) => {
  const [activeHardware, setActiveHardware] = useState<
    undefined | HardwareType
  >(props.hardwaresList.find((h) => h.id === props.activeHardwareId))
  const apiRef = useGridApiRef()

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteID, setDeleteID] = useState<null | number>(null)
  const [ifSureCheckbox, setIfSureCheckbox] = useState(false)
  const [usedHardwaresInDtools, setUsedHardwaresInDtools] = useState<
    { id: number; tooltip: string }[]
  >([])
  const [selectedTags, setSelectedTags] = useState<string[]>([])

  const allTags = Array.from(
    new Set(
      props.dtoolRelationsList
        .filter((r) => r.type === 'hardware')
        .flatMap((r) => r.tags)
    )
  ).sort()

  const { enqueueSnackbar } = useSnackbar()

  const handleCopyHardwareClick = (id: number) => {
    props.copyHardware(id, enqueueSnackbar)
  }

  const handleDeleteHardwareClick = (id: number) => {
    setDeleteID(id)
    setDeleteDialogOpen(true)
  }

  const handleDeleteDialogClose = () => {
    setDeleteID(null)
    setDeleteDialogOpen(false)
  }

  const handleDeleteDialogDeleteClick = () => {
    if (deleteID) props.deleteHardware(deleteID, enqueueSnackbar)
    handleDeleteDialogClose()
  }

  useEffect(() => {
    setActiveHardware(
      props.hardwaresList.find((h) => h.id === props.activeHardwareId)
    )
  }, [props.activeHardwareId])

  useEffect(() => {
    if (activeHardware && props.dtoolRelationsList) {
      setTimeout(() => {
        try {
          const rowIndex = apiRef.current.getRowIndexRelativeToVisibleRows(
            activeHardware.id
          )
          if (rowIndex !== undefined && rowIndex !== -1) {
            apiRef.current.scrollToIndexes({ rowIndex })
          }
        } catch (e) {
          // apiRef might not be fully initialized or row not found
        }
      }, 100)
    }
  }, [activeHardware, props.dtoolRelationsList, apiRef])

  useEffect(() => {
    saveGridStateInLocalStorage(
      {
        visibilityModel: props.hardwareGridVisibilityModel,
        filterModel: props.hardwareGridFilterModel,
        sortModel: props.hardwareGridSortModel,
      },
      'hardware'
    )
  }, [
    props.hardwareGridVisibilityModel,
    props.hardwareGridFilterModel,
    props.hardwareGridSortModel,
  ])

  useEffect(() => {
    const arr = []
    const hIds = props.hardwaresList.map((h) => h.id)
    for (const dtoolR of props.dtoolRelationsList.filter(
      (r) => r.type === 'hardware'
    )) {
      if (hIds.includes(dtoolR.targetId)) {
        arr.push({
          id: dtoolR.targetId,
          tooltip: `Hardware is used in Dtool: ${dtoolR.tags.join()}`,
        })
      }
    }
    setUsedHardwaresInDtools(arr)
  }, [props.dtoolRelationsList, props.hardwaresList])

  return (
    <Box display="flex" style={{ height: window.innerHeight - 48 }}>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        pt={2}
        style={{ borderRight: 'solid 1px black', flexGrow: 1 }}
      >
        <Typography variant="h6">Parts List</Typography>

        {allTags.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              my: 1,
              justifyContent: 'center',
              width: '90%',
            }}
          >
            {allTags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                color={selectedTags.includes(tag) ? 'primary' : 'default'}
                onClick={() => {
                  if (selectedTags.includes(tag)) {
                    setSelectedTags(selectedTags.filter((t) => t !== tag))
                  } else {
                    setSelectedTags([...selectedTags, tag])
                  }
                }}
              />
            ))}
          </Box>
        )}

        <Paper style={{ width: '90%', maxHeight: '90%', overflowY: 'auto' }}>
          <List component="nav" style={{ padding: 0 }}>
            <ListItem
              button
              onClick={() => {
                props.setActiveHardwareId(0)
              }}
            >
              <ListItemText primary="New Part" />
              <AddIcon />
            </ListItem>
            <Divider />
            {/* {
                            props.hardwaresList.map(h => <Fragment key={h.name}>
                                <ListItem button onClick={() => { setActiveHardware(h) }}>
                                    <ListItemText primary={h.name} />
                                    <ListItemSecondaryAction>
                                        <IconButton edge="end" onClick={() => { handleCopyHardwareClick(h.id) }}>
                                            <CopyIcon />
                                        </IconButton>
                                        <IconButton edge="end" onClick={() => { handleDeleteHardwareClick(h.id) }}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                </ListItem>
                                <Divider />
                            </Fragment>)
                        } */}
          </List>
        </Paper>
        <DataGrid
          apiRef={apiRef}
          style={{ width: '90%', maxHeight: '90%', overflowY: 'auto' }}
          columns={[
            {
              field: 'tags',
              headerName: 'Tags',
              width: 150,
              renderCell: (params: GridRenderCellParams) => (
                <Box
                  sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 0.5 }}
                >
                  {(params.value as string[]).map((tag, idx) => (
                    <Chip
                      key={idx}
                      label={tag}
                      color="primary"
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        '& .MuiChip-label': { px: 1 },
                      }}
                    />
                  ))}
                </Box>
              ),
            },
            { field: 'id' },
            {
              field: 'date',
              headerName: 'Last Modified',
              type: 'datetime',
              minWidth: 160,
            },
            { field: 'name', headerName: 'Name', minWidth: 200 },
            { field: 'type', headerName: 'Type', width: 150 },
            { field: 'description', headerName: 'Description', width: 200 },
            { field: 'unitPrice', headerName: 'Unit Price', width: 110 },
            { field: 'markup', headerName: 'Markup', width: 110 },
            {
              field: 'materialBased',
              headerName: 'Material Based',
              width: 120,
            },
            { field: 'materialIds', headerName: 'Material Ids', width: 200 },
            {
              field: 'defaultMaterialId',
              headerName: 'Default Material Id',
              width: 120,
            },
            { field: 'children', headerName: 'Children Ids', width: 120 },
            {
              field: 'actions',
              headerName: 'Actions',
              filterable: false,
              sortable: false,
              renderCell: (params: GridRenderCellParams) => (
                <Box
                  onClick={(e) => {
                    e.stopPropagation() // don't select this row after clicking
                  }}
                >
                  <IconButton
                    edge="end"
                    onClick={() => {
                      handleCopyHardwareClick(parseInt(params.id.toString()))
                    }}
                  >
                    <CopyIcon />
                  </IconButton>
                  {props.hardwaresList.find(
                    (h) => h.id === parseInt(params.id.toString())
                  )?.editable === '1' &&
                    (() => {
                      const disabledItem = usedHardwaresInDtools.find(
                        (i) => i.id === parseInt(params.id.toString())
                      )
                      return (
                        <Tooltip
                          title={disabledItem ? disabledItem.tooltip : ''}
                        >
                          <span>
                            <IconButton
                              edge="end"
                              disabled={!!disabledItem}
                              onClick={() => {
                                handleDeleteHardwareClick(
                                  parseInt(params.id.toString())
                                )
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )
                    })()}
                </Box>
              ),
              width: 150,
            },
          ]}
          // hideFooterPagination
          rows={props.hardwaresList
            .map((hardware) => {
              const relatedDtools = props.dtoolRelationsList.filter(
                (r) => r.type === 'hardware' && r.targetId === hardware.id
              )
              const tags = Array.from(
                new Set(relatedDtools.flatMap((r) => r.tags))
              )

              return {
                id: hardware.id,
                tags: tags,
                date: new Date(hardware.date),
                name: hardware.name,
                type: hardware.type,
                description: hardware.description,
                unitPrice: hardware.unitPrice,
                markup:
                  props.markups.find((m) => m.id === hardware.markupId)?.name ||
                  hardware.markupId,
                materialBased: hardware.materialsIds.length > 0 ? '✓' : '',
                materialIds: hardware.materialsIds.join(','),
                defaultMaterialId: hardware.defaultMaterialId,
                children: hardware.childrenIds.join(','),
              }
            })
            .filter(
              (row) =>
                selectedTags.length === 0 ||
                selectedTags.some((t) => row.tags.includes(t))
            )}
          onRowClick={(param) => {
            props.setActiveHardwareId(parseInt(param.id.toString()))
          }}
          columnVisibilityModel={props.hardwareGridVisibilityModel}
          onColumnVisibilityModelChange={(model) => {
            props.setHardwareGridVisibilityModel(model)
          }}
          sortModel={props.hardwareGridSortModel}
          onSortModelChange={(model) => {
            if (
              JSON.stringify(model) !==
              JSON.stringify(props.hardwareGridSortModel)
            )
              props.setHardwareGridSortModel(model)
          }}
          filterModel={props.hardwareGridFilterModel}
          onFilterModelChange={(model) => {
            if (
              JSON.stringify(model) !==
              JSON.stringify(props.hardwareGridFilterModel)
            )
              props.setHardwareGridFilterModel(model)
          }}
          // onStateChange={(state) => {
          //     for (const columnName in state.columns.lookup) {
          //         const column = state.columns.lookup[columnName]
          //         const stateColumn = props.hardwareGridVisibilityModel.find(c => c.field === columnName)

          //         if (stateColumn) {
          //             if (column.hide !== stateColumn.hide) {
          //                 props.setHardwareGridVisibilityModel(columnName, column.hide || false)
          //             }
          //         }
          //     }
          // }}

          showCellVerticalBorder
          rowSelectionModel={props.activeHardwareId}
        />
      </Box>
      {activeHardware === undefined ? (
        <DefineHardware
          markups={props.markups}
          hardwareList={props.hardwaresList}
          materials={props.materials}
          materialGroups={props.materialGroups}
          addMarkup={props.addMarkup}
          addHardware={props.addHardware}
          openMaterialDetails={props.openMaterialDetails}
          openMaterialGroupDetails={props.openMaterialGroupDetails}
        />
      ) : (
        <EditHardware
          markups={props.markups}
          hardwareList={props.hardwaresList}
          materials={props.materials}
          materialGroups={props.materialGroups}
          addMarkup={props.addMarkup}
          hardware={activeHardware}
          editHardware={props.editHardware}
          openMaterialDetails={props.openMaterialDetails}
          openMaterialGroupDetails={props.openMaterialGroupDetails}
        />
      )}

      <Dialog open={deleteDialogOpen}>
        <DialogTitle>Delete Part</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Are you sure you want to delete{' '}
            <b>
              "
              {
                props.hardwaresList.find((hardware) => hardware.id === deleteID)
                  ?.name
              }
              "
            </b>{' '}
            part?
          </Typography>
          <br />
          {props.hardwaresList.filter((h) =>
            h.childrenIds.includes(deleteID ? deleteID : 0)
          ).length > 0 && (
            <>
              <Typography>Part is child of</Typography>
              {props.hardwaresList
                .filter((h) => h.childrenIds.includes(deleteID ? deleteID : 0))
                .map((h) => (
                  <Typography key={h.id} component="li">
                    {h.name}
                  </Typography>
                ))}
              <br />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={ifSureCheckbox}
                    onChange={(e) => {
                      setIfSureCheckbox(e.target.checked)
                    }}
                  />
                }
                label="Yes, I'm sure!"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteDialogDeleteClick}
            color="secondary"
            disabled={
              props.hardwaresList.filter((h) =>
                h.childrenIds.includes(deleteID ? deleteID : 0)
              ).length > 0
                ? !ifSureCheckbox
                : false
            }
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

const mapStateToProps = (state: StateType) => ({
  hardwaresList: state.itemReducer.hardwaresList,
  markups: state.itemReducer.markups,
  materials: state.itemReducer.materials,
  materialGroups: state.itemReducer.materialGroups,
  hardwareGridVisibilityModel: state.appReducer.hardwareGrid.visibilityModel,
  hardwareGridSortModel: state.appReducer.hardwareGrid.sortModel,
  hardwareGridFilterModel: state.appReducer.hardwareGrid.filterModel,
  activeHardwareId: state.appReducer.activeHardwareId,
  dtoolRelationsList: state.appReducer.dtoolRelationsList,
})

export default connect(mapStateToProps, {
  addHardware,
  editHardware,
  deleteHardware,
  copyHardware,
  addMarkup,
  setHardwareGridVisibilityModel: appActions.setHardwareGridVisibilityModel,
  setHardwareGridSortModel: appActions.setHardwareGridSortModel,
  setHardwareGridFilterModel: appActions.setHardwareGridFilterModel,
  setActiveHardwareId: appActions.setActiveHardwareId,
  openMaterialDetails: appActions.openMaterialDetails,
  openMaterialGroupDetails: appActions.openMaterialGroupDetails,
})(HardwareTab)
